import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { useState } from 'react'

interface BudgetAllocation {
  education: number
  healthcare: number
  defense: number
  infrastructure: number
  social_services: number
  environment: number
  science: number
  administration: number
  economy: number
  security: number
  culture: number
  agriculture: number
  energy: number
}

function BudgetPage() {
  const { gameId } = useParams<{ gameId: string }>()
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const [budget, setBudget] = useState<BudgetAllocation>({
    education: 15,
    healthcare: 15,
    defense: 10,
    infrastructure: 12,
    social_services: 8,
    environment: 5,
    science: 7,
    administration: 8,
    economy: 6,
    security: 5,
    culture: 3,
    agriculture: 3,
    energy: 3
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const budgetSectors = [
    { key: 'education', name: 'Education', description: 'Schools, universities, and educational programs', color: 'blue' },
    { key: 'healthcare', name: 'Healthcare', description: 'Hospitals, medical research, and public health', color: 'green' },
    { key: 'defense', name: 'Defense', description: 'Military and national security', color: 'red' },
    { key: 'infrastructure', name: 'Infrastructure', description: 'Roads, bridges, and public transport', color: 'gray' },
    { key: 'social_services', name: 'Social Services', description: 'Welfare, unemployment benefits, and social support', color: 'purple' },
    { key: 'environment', name: 'Environment', description: 'Environmental protection and climate initiatives', color: 'green' },
    { key: 'science', name: 'Science & Technology', description: 'Research and technological advancement', color: 'indigo' },
    { key: 'administration', name: 'Administration', description: 'Government operations and civil service', color: 'yellow' },
    { key: 'economy', name: 'Economic Development', description: 'Business support and economic growth programs', color: 'emerald' },
    { key: 'security', name: 'Internal Security', description: 'Police and domestic security', color: 'red' },
    { key: 'culture', name: 'Culture & Arts', description: 'Cultural programs and arts funding', color: 'pink' },
    { key: 'agriculture', name: 'Agriculture', description: 'Farming support and food security', color: 'amber' },
    { key: 'energy', name: 'Energy', description: 'Energy infrastructure and renewable sources', color: 'orange' }
  ]

  const totalBudget = Object.values(budget).reduce((sum, val) => sum + val, 0)

  const handleBudgetChange = (sector: keyof BudgetAllocation, value: number) => {
    setBudget(prev => ({
      ...prev,
      [sector]: Math.max(0, Math.min(100, value))
    }))
  }

  const submitBudget = async () => {
    if (Math.abs(totalBudget - 100) > 0.1) {
      setMessage('Budget must total exactly 100%')
      return
    }

    try {
      setLoading(true)
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/games/${gameId}/budget`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(budget)
      })

      const data = await response.json()
      if (data.success) {
        setMessage('Budget allocated successfully!')
        setTimeout(() => navigate(`/game/${gameId}`), 2000)
      } else {
        setMessage(data.error || 'Failed to allocate budget')
      }
    } catch (error) {
      setMessage('Error submitting budget')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Budget Management</h1>
        <button 
          onClick={() => navigate(`/game/${gameId}`)}
          className="btn btn-secondary"
        >
          Back to Game
        </button>
      </div>

      {/* Budget Total */}
      <div className="card mb-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Total Budget Allocation</h2>
          <div className={`text-2xl font-bold ${Math.abs(totalBudget - 100) < 0.1 ? 'text-green-600' : 'text-red-600'}`}>
            {totalBudget.toFixed(1)}%
          </div>
        </div>
        <div className="mt-2 w-full bg-gray-200 rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-all ${Math.abs(totalBudget - 100) < 0.1 ? 'bg-green-500' : 'bg-red-500'}`}
            style={{ width: `${Math.min(totalBudget, 100)}%` }}
          ></div>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          {totalBudget > 100 ? 'Over budget! ' : totalBudget < 100 ? 'Under budget. ' : 'Perfect allocation! '}
          Budget must total exactly 100%.
        </p>
      </div>

      {/* Budget Sectors */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {budgetSectors.map(sector => (
          <div key={sector.key} className="card">
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-semibold text-gray-900">{sector.name}</h3>
              <span className="text-lg font-bold text-gray-700">
                {budget[sector.key as keyof BudgetAllocation].toFixed(1)}%
              </span>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">{sector.description}</p>
            
            <div className="space-y-3">
              <input
                type="range"
                min="0"
                max="50"
                step="0.1"
                value={budget[sector.key as keyof BudgetAllocation]}
                onChange={(e) => handleBudgetChange(sector.key as keyof BudgetAllocation, parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              
              <div className="flex justify-between items-center">
                <button
                  onClick={() => handleBudgetChange(sector.key as keyof BudgetAllocation, Math.max(0, budget[sector.key as keyof BudgetAllocation] - 1))}
                  className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                >
                  -1%
                </button>
                
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={budget[sector.key as keyof BudgetAllocation]}
                  onChange={(e) => handleBudgetChange(sector.key as keyof BudgetAllocation, parseFloat(e.target.value) || 0)}
                  className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm"
                />
                
                <button
                  onClick={() => handleBudgetChange(sector.key as keyof BudgetAllocation, Math.min(50, budget[sector.key as keyof BudgetAllocation] + 1))}
                  className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                >
                  +1%
                </button>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${(budget[sector.key as keyof BudgetAllocation] / 50) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      {/* Submit Button */}
      <div className="card">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Submit Budget for This Turn</h3>
            <p className="text-sm text-gray-600">
              This will finalize your budget allocation for the current turn. Make sure the total equals 100%.
            </p>
          </div>
          
          <button
            onClick={submitBudget}
            disabled={loading || Math.abs(totalBudget - 100) > 0.1}
            className={`px-6 py-3 font-semibold rounded-lg ${
              Math.abs(totalBudget - 100) < 0.1 && !loading
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? 'Submitting...' : 'Submit Budget'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default BudgetPage